import React from 'react';
import Link from 'next/link';
import Card from '../Card';
import Reveal, { revealStagger } from '../Reveal';
import Section from '../Section';
import Eyebrow from '../Eyebrow';
import { FPointMarkIcon, RingArcIcon, RingDotIcon, TripleRingIcon } from '../icons/service-icons';

/**
 * The four ways to engage.
 *
 * Pillar 1 is the reversed card on --ink-blue. That is an agreed override of
 * the design reference, which reverses the middle of three: the brief makes
 * pillar 1 the standalone partner-sellable offering (brief section 6) and it is
 * the only pillar with a destination page, so it is the one that should read as
 * primary.
 *
 * The reference's per-card duration eyebrow and closing accent line are not
 * ported. The four pillars supply neither, and porting them would mean
 * inventing copy.
 *
 * The section heading is the one string here that neither the ticket nor the
 * brief supplies. It is the design reference's own services heading with its
 * numeral corrected from three to four.
 *
 * Marks come from the shared ring-and-dot family and are driven by
 * currentColor, so the reversed card recolours its mark by setting a text
 * colour on the wrapper rather than taking a variant prop.
 *
 * /ai-adoption does not exist until #7922 merges. Accepted: #7919 ships the
 * same nav item ahead of the route and #7918 holds the deploy until both land.
 */

interface Pillar {
  title: string;
  body: string;
  icon: React.ComponentType<{ size?: number; className?: string }>;
  href?: string;
  reversed?: boolean;
}

const PILLARS: Pillar[] = [
  {
    title: 'AI Adoption, Executed',
    body: 'Most AI initiatives stall after the strategy deck. I run discovery with your teams, identify the use cases that actually pay back, then build and roll them out. Workshops, hands-on implementation, and the training that makes it stick.',
    icon: FPointMarkIcon,
    href: '/ai-adoption',
    reversed: true,
  },
  {
    title: 'Product & Executive Partnership',
    body: 'Fractional CPO and strategic product leadership for scaling organizations. Roadmaps, prioritization, and the operating rhythm to deliver against them. Enough process to hold under pressure, never more than the situation needs.',
    icon: RingDotIcon,
  },
  {
    title: 'Build and Ship',
    body: 'Production systems built fast using AI-native development. Working software instead of specifications, from prototype through to live deployment.',
    icon: TripleRingIcon,
  },
  {
    title: 'Advisory & Board Representation',
    body: 'Governance, risk and scaling insight for high-stakes ventures, from someone who has been through acquisitions on both sides.',
    icon: RingArcIcon,
  },
];

function PillarCard({ pillar }: { pillar: Pillar }) {
  const Icon = pillar.icon;

  return (
    <Card variant={pillar.reversed ? 'reversed' : 'default'} className="flex h-full flex-col gap-3">
      <Icon className={pillar.reversed ? 'text-sand-accent' : 'text-ink-blue'} />
      <h3
        className={`font-display text-[22px] font-medium tracking-[-0.02em] ${
          pillar.reversed ? 'text-on-blue' : 'text-ink'
        }`}
      >
        {pillar.title}
      </h3>
      <p
        className={`font-sans text-base leading-[1.65] ${
          pillar.reversed ? 'text-on-blue-2' : 'text-body'
        }`}
      >
        {pillar.body}
      </p>
    </Card>
  );
}

export default function ServicesSection() {
  return (
    <Section id="services">
      <Reveal>
        <Eyebrow>What I do</Eyebrow>

        <h2 className="mt-[18px] max-w-[760px] font-display text-[32px] font-medium leading-[1.08] tracking-[-0.032em] text-balance text-ink md:text-[42px]">
          Four ways in, all ending in something that works.
        </h2>
      </Reveal>

      {/* The Reveal is the grid child and carries h-full, so the card's own
          h-full still resolves against the grid area and the four cards stay
          equal height. */}
      <div className="grid gap-6 pt-11 md:grid-cols-2 lg:grid-cols-4">
        {PILLARS.map((pillar, index) => (
          <Reveal key={pillar.title} className="h-full" delay={revealStagger(index)}>
            {pillar.href ? (
              <Link
                href={pillar.href}
                className="block h-full rounded-xl transition-opacity duration-200 hover:opacity-90"
              >
                <PillarCard pillar={pillar} />
              </Link>
            ) : (
              <PillarCard pillar={pillar} />
            )}
          </Reveal>
        ))}
      </div>

      <Reveal>
        <p className="pt-7 font-sans text-base leading-[1.65] text-caption">
          Also available for smaller engagements: high-impact web builds, and digital growth and
          SEO work.
        </p>
      </Reveal>
    </Section>
  );
}
